/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Hightouch.Substitutions = NO -->*/
/*<!-- vale Hightouch.Substitutions = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Git Sync", description = "Git Sync allows you to version-control your Hightouch settings via Git.", lastUpdatedDate = new Date(1747958400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Git Sync",
        description: "Git Sync allows you to version-control your Hightouch settings via Git.",
        lastUpdatedDate: "Fri May 23 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Authenticate to Git",
          "depth": 1,
          "id": "authenticate-to-git"
        }, {
          "value": "Configure Git Sync",
          "depth": 1,
          "id": "configure-git-sync"
        }, {
          "value": "Using Git Sync with branch protection",
          "depth": 0,
          "id": "using-git-sync-with-branch-protection"
        }, {
          "value": "Schemas",
          "depth": 0,
          "id": "schemas"
        }, {
          "value": "Model schema",
          "depth": 1,
          "id": "model-schema"
        }, {
          "value": "Sync schemas",
          "depth": 1,
          "id": "sync-schemas"
        }, {
          "value": "Customer Studio Schema",
          "depth": 1,
          "id": "customer-studio-schema"
        }, {
          "value": "Event Contract Schema",
          "depth": 1,
          "id": "event-contract-schema"
        }, {
          "value": "Manifests",
          "depth": 1,
          "id": "manifests"
        }, {
          "value": "Aliases",
          "depth": 1,
          "id": "aliases"
        }, {
          "value": "Validating changes",
          "depth": 0,
          "id": "validating-changes"
        }, {
          "value": "Changelog",
          "depth": 0,
          "id": "changelog"
        }, {
          "value": "Tunneling",
          "depth": 0,
          "id": "tunneling"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "What does the Full resync button do?",
          "depth": 1,
          "id": "what-does-the-full-resync-button-do"
        }, {
          "value": "Recreating a deleted YAML file",
          "depth": 1,
          "id": "recreating-a-deleted-yaml-file"
        }, {
          "value": "Protected branch update failed",
          "depth": 1,
          "id": "protected-branch-update-failed"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      img: "img",
      a: "a",
      h2: "h2",
      ol: "ol",
      h3: "h3",
      strong: "strong",
      h4: "h4",
      em: "em",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {BtOnly, Video, PylonLink} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!Video) _missingMdxReference("Video", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Git Sync"
      }), "\n", _jsx(Video, {
        source: "https://cdn.sanity.io/files/pwmfmi47/production/28b600cb8a0e813b85206e5fbd2f44db9ebac380.mp4",
        poster: "https://cdn.sanity.io/images/pwmfmi47/production/268d6fd4a5a5723ed2f0815a1ecdc63988180f71-3024x1700.webp",
        title: "Git Sync and dbt Extensions Overview",
        description: "Learn about how Hightouch's Git and dbt extensions work.",
        duration: "4 min",
        uploadDate: "2022-1-31T08:00:00+08:00"
      }), "\n", "\n", _jsx(_components.p, {
        children: "Git Sync is a bi-directional integration between your Hightouch workspace and a Git repository. It brings all the benefits of version control to your Data Activation workflows. For example, it lets you:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Use code to create and edit multiple Hightouch models and syncs simultaneously"
        }), "\n", _jsx(_components.li, {
          children: "Create commit logs of incremental changes"
        }), "\n", _jsx(_components.li, {
          children: "Roll back your resources to a previous state"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When enabled, Git Sync saves updates to resources made in the Hightouch UI to YAML files in a Git repository. The extension marks changes made in the Hightouch UI as ", _jsx(_components.code, {
          children: "OUTBOUND"
        }), ". Each update to a resource also becomes a commit in your designated repository."]
      }), "\n", _jsx(_components.p, {
        children: "Git Sync currently supports models (including Customer Studio models), syncs, and event contracts. Git Sync does not currently support Customer Studio audiences or syncs from those audiences."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync.png",
          alt: "Git Sync diagram"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The sync also works in the opposite direction. Any changes you make to the YAML files—including making new YAML files—updates or creates resources in Hightouch as if you made them in the UI. The extension marks these as ", _jsx(_components.code, {
          children: "INBOUND"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Inbound changes made in your Git Sync repository don't require approval from\nanother team member if your workspace is using ", _jsx(_components.a, {
            href: "/workspace-management/approval-flows",
            children: "approval\nflows"
          }), "."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Git Sync supports virtually all Git providers, including GitHub, Bitbucket, GitLab, and private ", _jsx(_components.a, {
          href: "#tunneling",
          children: "on-premise"
        }), " repositories. Regardless of direction, the extension pushes changes between the Hightouch UI and Git repository in about one minute."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch can communicate with two distinct Git repository types. The Git Sync\nfeature interacts with a Git repository to version control your Hightouch\nsettings. This is separate from Git repositories used to ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "leverage dbt\nmodels"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "You must complete three steps to use Git Sync:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Create a Git repository—if you don't already have one—to store your schema files. GitHub repositories started from scratch are the easiest to work with, as they can make use of the Hightouch GitHub bot."
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "If you create an empty repository, make sure you also make an initial commit,\nfor example, creating a README file, so that a branch exists."
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsx(_components.li, {
          children: "Authenticate Hightouch access to your Git repository."
        }), "\n", _jsx(_components.li, {
          children: "Configure Git Sync for your workspace."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-to-git",
        children: "Authenticate to Git"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you have a repository, you can configure your Git credentials in Hightouch.\nGo to the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/git-sync/configuration",
          children: "extension configuration tab"
        }), " and click ", _jsx(_components.strong, {
          children: "Set up"
        }), " under ", _jsx(_components.strong, {
          children: "Configuration > Git credentials"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you're using GitHub, it's best to use ", _jsx(_components.a, {
          href: "https://github.com/apps/hightouch-connect/",
          children: "Hightouch's GitHub app"
        }), " for the most up-to-date feature support."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-github-app.png",
          alt: "Selecting the GitHub app"
        })
      }), "\n", _jsx(_components.p, {
        children: "Regardless of how you connect to your Git provider, you need to provide permissions to the particular repository or repositories Hightouch should have access to."
      }), "\n", _jsx(_components.h4, {
        id: "authenticate-to-a-github-enterprise-repository",
        children: "Authenticate to a GitHub Enterprise repository"
      }), "\n", _jsxs(_components.p, {
        children: ["If you're using GitHub Enterprise with a self-hosted server, select ", _jsx(_components.strong, {
          children: "Other"
        }), " under ", _jsx(_components.strong, {
          children: "Git service"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-github-enterprise.png",
          alt: "Configuring GitHub Enterprise"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.strong, {
          children: "Username"
        }), ", enter your ", _jsx(_components.em, {
          children: "full GitHub Enterprise username"
        }), " which may look like something like \"", _jsx(_components.a, {
          href: "mailto:hightouch123@github.hightouch.com",
          children: "hightouch123@github.hightouch.com"
        }), ".\"\nFor ", _jsx(_components.strong, {
          children: "Token"
        }), ", enter a ", _jsx(_components.a, {
          href: "https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/creating-a-personal-access-token",
          children: "GitHub access token"
        }), " tied to the username you entered."]
      }), "\n", _jsx(_components.p, {
        children: "Be sure the token provides access to the particular repository or repositories Hightouch should have access to."
      }), "\n", _jsx(_components.h4, {
        id: "authenticate-to-a-aws-codecommit-repository",
        children: "Authenticate to a AWS CodeCommit repository"
      }), "\n", _jsxs(_components.p, {
        children: ["If you use AWS CodeCommit to host your Git repositories, you must use ", _jsx(_components.strong, {
          children: "SSH"
        }), " to authenticate to Hightouch."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Configure your AWS CodeCommit repository for an SSH connection by following ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/codecommit/latest/userguide/setting-up-ssh-unixes.html",
            children: "these instructions"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In Hightouch, select ", _jsx(_components.strong, {
            children: "Other"
          }), " as the ", _jsx(_components.strong, {
            children: "Git service"
          }), " and ", _jsx(_components.strong, {
            children: "SSH"
          }), " as the ", _jsx(_components.strong, {
            children: "Protocol"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-ssh-auth.png",
          alt: "Configuring AWS CodeCommit"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Enter the private key you generated when configuring AWS CodeCommit repository under ", _jsx(_components.strong, {
            children: "SSH Private key"
          }), ". Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["For the ", _jsx(_components.strong, {
            children: "Repository"
          }), ", enter the full SSH URL including ", _jsx(_components.strong, {
            children: "AWS SSH Key ID"
          }), ". This key is different than the generic ", _jsx(_components.strong, {
            children: "Access keys"
          }), " in your AWS account."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-aws-ssh-id-1.png",
          alt: "Configuring AWS CodeCommit"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-aws-ssh-id-2.png",
          alt: "Configuring AWS CodeCommit"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can copy your URL from these ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/codecommit/latest/userguide/how-to-connect.html",
          children: "instructions in AWS's docs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-aws-docs.png",
          alt: "Configuring AWS CodeCommit"
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-git-sync",
        children: "Configure Git Sync"
      }), "\n", _jsxs(_components.p, {
        children: ["Last, you need to enable and configure Git Sync for your workspace.\nGo to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/extensions/git-sync/configuration",
          children: ["Git Sync ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab"]
        }), " on the Extensions page and select the ", _jsx(_components.strong, {
          children: "Repository"
        }), " and ", _jsx(_components.strong, {
          children: "Branch"
        }), " where you'd like changes saved to and read from."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-config.png",
          alt: "Git Sync configuration in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Optionally, enter a ", _jsx(_components.strong, {
          children: "Path"
        }), " where the integration should create the sync and models folders.\nFor example, the preceding screenshot configures saving changes to a ", _jsx(_components.code, {
          children: "./hightouch"
        }), " subfolder.\nHightouch creates folders for syncs and models within this subfolder. If you don't enter a path, it saves them in the root of your Git repository."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-repo-synced.png",
          alt: "Models and syncs folders in a Git repository"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To save your Git Sync configuration, click the ", _jsx(_components.strong, {
          children: "Save"
        }), " button on the right side.\nAfter clicking save, Hightouch creates the subfolder if needed.\nThe extension then processes the bi-directional sync from Hightouch to Git and vice-versa.\nSpecifically, the extension starts writing your models, syncs, and event contracts as YAML files to your repository.\nIf you create any new supported resources in the repository, the extension creates them in Hightouch."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["To begin the syncing process immediately, click the ", _jsx(_components.strong, {
            children: "Full Resync"
          }), " button.\nThis action pushes all workspace syncs and models to Git and then pulls\nchanges from Git back to your workspace, in that order. This may overwrite\nchanges in Git that have not yet been synced to Hightouch. For more\ninformation, please check the ", _jsx(_components.a, {
            href: "#what-does-the-full-resync-button-do",
            children: "FAQ\nsection"
          }), "."]
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["For safety reasons, the extension doesn't support deletions from Git. If you\ndelete a YAML file in Git, the corresponding model or sync remains in\nHightouch. The file won't be rewritten to Git until the next change in\nHightouch, or unless you trigger a ", _jsx(_components.a, {
            href: "#what-does-the-full-resync-button-do",
            children: "full\nresync"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "using-git-sync-with-branch-protection",
        children: "Using Git Sync with branch protection"
      }), "\n", _jsx(_components.p, {
        children: "If you use branch protection to protect your branches from unauthorized pushes and/or require pull requests before merges can happen, you need to grant bypass access to the Hightouch Connect app. The Hightouch Connect app is only able to push to your branch if it is added to the bypass list under rulesets."
      }), "\n", _jsx(_components.p, {
        children: "In Github, go to 'Settings' -> 'Rules' -> 'Rulesets' and add the Hightouch Connect app to the 'Bypass list'. Configure the ruleset to disallow the actions you'd like to restrict."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "In Github, there are two places where you can enforce branch protection,\neither in 'Settings' under 'Branches' or under 'Rulesets'. The Hightouch\nConnect app only works if you add it to the 'Bypass list' under 'Rulesets' and\nhave the rest of your branch protection conditions defined under 'Rulesets'.\nIt does not work as a 'force push' app under 'Branches'."
        })
      }), "\n", _jsx(_components.h2, {
        id: "schemas",
        children: "Schemas"
      }), "\n", _jsx(_components.p, {
        children: "You can expect the YAML files in your Git repository to use the following schemas."
      }), "\n", _jsx(_components.h3, {
        id: "model-schema",
        children: "Model schema"
      }), "\n", _jsx(_components.p, {
        children: "Models have a consistent schema:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "model"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name/slug."
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "unique"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "per"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "model"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "source:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name/slug"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "source."
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "slug"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "is"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "visible"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "on"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "source"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "page"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "one of:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "raw_sql,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "table,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "dbt_model"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "dbtModel:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "dbt"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "model"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "select,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "written"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "as"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "`model.<package_name>.<model_name>`"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "rawSql:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "sql"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "use,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "example,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "select"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "albums"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "tableName:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "table"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "use"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "isSchema:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "used"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "internally"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "by"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Hightouch,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "should"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "always"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "be"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "primaryKey:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "primary"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "key"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "column"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "model,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "example,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "album_id"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "sync-schemas",
        children: "Sync schemas"
      }), "\n", _jsx(_components.p, {
        children: "The schema for syncs is destination-dependent. For this reason, it's best to initially create syncs within the UI. You can then use the schema created in Git Sync to update syncs or create new ones. The name of the YAML file is the name of the sync."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "model:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "model"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name/slug"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "sync."
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "destination:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "destination"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name/slug"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "sync"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "config:"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "destination"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "dependent"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "configuration"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "sync."
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "schedule:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "type"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "schedule"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "use."
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "format"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "here"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "varies"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "based"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "on"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-string",
            children: "schedule"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "selected,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "we"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "recommend"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "using"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "UI"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "as"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "a"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "starting"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "point."
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "schedulePaused:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "boolean,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "where"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "sync"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "is"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "paused,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "enabled."
          }), "\n"]
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Based on the boolean value assigned to ", _jsx(_components.code, {
            children: "schedulePaused"
          }), ", your sync will either\nbe ", _jsx(_components.a, {
            href: "/syncs/overview#individual-sync-overview-page",
            children: "enabled or disabled"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "customer-studio-schema",
        children: "Customer Studio Schema"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Support for Customer Studio schema models is currently a beta feature. Please ", _jsx(PylonLink, {
            pylonText: "Hi, I'm interested in Git Sync for Customer Studio schema.",
            linkText: "reach out"
          }), " if you're interested in this feature."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "parent-model-schema",
        children: "Parent model schema"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/schema#parent-model-setup",
          children: "Parent models"
        }), " live in the ", _jsx(_components.code, {
          children: "schema/parent-models"
        }), " directory. They have the same schema as ", _jsx(_components.a, {
          href: "#model-schema",
          children: "regular models"
        }), ", with the following additions:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "primaryLabel:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"primary_label_column\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Optional"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "secondaryLabel:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"secondary_label_column\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Optional"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "related-model-schema",
        children: "Related model schema"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/schema#related-models-and-events",
          children: "Related models"
        }), " live in the ", _jsx(_components.code, {
          children: "schema/related-models"
        }), " directory. They have the same schema as ", _jsx(_components.a, {
          href: "#model-schema",
          children: "regular models"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "event-model-schema",
        children: "Event model schema"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/schema#related-models-and-events",
          children: "Event models"
        }), " live in the ", _jsx(_components.code, {
          children: "schema/event-models"
        }), " directory. They have the same schema as ", _jsx(_components.a, {
          href: "#model-schema",
          children: "regular models"
        }), ", with the following addition:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "eventTimestampColumn:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"column_name\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Required"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "model-relationship-schemas",
        children: "Model relationship schemas"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/schema#relationships",
          children: "Model relationships"
        }), " live in the ", _jsx(_components.code, {
          children: "schema/model-relationships"
        }), " directory."]
      }), "\n", _jsx(_components.p, {
        children: "The schema for model relationships depends on whether the relationship is direct or through other relationships."
      }), "\n", _jsx(_components.p, {
        children: "Direct relationships look like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "from:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "relationshipName:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Relationship name for from -> to"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "slug:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model_slug\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "mergeColumns:"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# or true"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "to:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "relationshipName:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Relationship name for to -> from"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "slug:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model_slug\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "mergeColumns:"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "cardinality:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"1:1\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# One of: \"1:1\", \"1:many\", \"many:1\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "mapping:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "fromColumn:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "toColumn:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Through relationships look like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"relationship name\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "from:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "slug:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model_slug\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "to:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "slug:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model_slug\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "through:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# List of relationship slugs that constitute this through relationship."
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "relationship_slug_1"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "relationship_slug_2"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "event-contract-schema",
        children: "Event Contract Schema"
      }), "\n", _jsx(_components.h4, {
        id: "event-contracts",
        children: "Event Contracts"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/events/contracts/management",
          children: "Event Contracts"
        }), " have a simple, consistent schema:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "event"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "contract"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "description:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "description"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "contract"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "onUndeclaredSchema:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "one"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ALLOW_EVENT\""
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "(default)"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"BLOCK_EVENT\""
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The name of the file is the slug of the event contract, which is derived from the name of the contract."
      }), "\n", _jsx(_components.h4, {
        id: "events",
        children: "Events"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/events/contracts/management#event-definition",
          children: "Event Schemas"
        }), " are YAML files that define the allowed format of events sent to Hightouch.\nEach file represents a single version of a single event within a contract."]
      }), "\n", _jsxs(_components.p, {
        children: ["The files are located in the ", _jsx(_components.code, {
          children: "events/contracts"
        }), " directory, within a subdirectory named after the contract.\nThe filename is the slug of the event, which is derived from the name or type, the contract slug, and the version."]
      }), "\n", _jsx(_components.p, {
        children: "The schema for an event is as follows:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "one"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"track\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"identify\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"group\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"page\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"screen\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alias\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "track"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "events,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "event"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name;"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "otherwise"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "should"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "not"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "be"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "present"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "version:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "version"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "event;"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "defaults"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"default\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "onSchemaViolation:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "one"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ALLOW_EVENT\""
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "(default)"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"BLOCK_EVENT\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "onUndeclaredFields:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "one"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ALLOW_EVENT\""
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "(default),"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"BLOCK_EVENT\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"OMIT_FIELDS\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "schema:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "|\n  Schema object for validating the event, adhering to the JSON Schema spec\n  Usually the root of this schema is an object which contains a nested object named properties\n"
          })]
        })
      }), "\n", _jsx(_components.p, {
        children: "In the following example, we have a Sample Event that contains a single optional \"id\" field:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# events/contracts/web/sample-event-web-default.yaml"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Sample"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Event"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "onSchemaViolation:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "ALLOW_EVENT"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "onUndeclaredFields:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "ALLOW_EVENT"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "schema:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "object"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# The root of our schema is an object"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties:"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Objects contain a \"properties\" element in the JSON Schema spec"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties:"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# We define a single child named properties"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "object"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Which is an object"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties:"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Finally, the properties our child object contains"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "id:"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Which is only an optional id field"
          }), "\n          ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "string"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Which is of type string"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "track"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "version:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "default"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "This corresponds to the following JSON in the in-app JSON Editor:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"object\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"object\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n          ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"string\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Because event schemas may get complex, using Hightouch ", _jsx(_components.a, {
          href: "#validating-changes",
          children: "CI checks"
        }), " is a great way to validate your changes\nbefore merging them into your repository."]
      }), "\n", _jsx(_components.h3, {
        id: "manifests",
        children: "Manifests"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch creates a manifest file at the root of your Git repository for each connected workspace. ", _jsx(_components.strong, {
          children: "Manifest files"
        }), " list all sources and destinations in a workspace, making it easier for you to find their IDs to add to sync and model files or reference in ", _jsx(_components.a, {
          href: "#aliases",
          children: "aliases"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Manifest filenames follow this format: ", _jsx(_components.code, {
          children: "manifest-<your-workspace-id>.yaml"
        }), ". A manifest file follows this format:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# READ ONLY FILE"
          }), "\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Changes made here will be ignored. Please use the Hightouch dashboard to create and edit sources and destinations."
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "production-postgres:"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Production"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "postgres"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "postgres"
          }), "\n   ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "snowflake-243-tenant:"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Snowflake"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "EU"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "instance"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "snowflake"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "destinations:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "production-salesforce:"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Production"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Salesforce"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "salesforce"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Sources and destinations are listed by their ID. In the example file above, the Postgres source's ID is ", _jsx(_components.code, {
          children: "production-postgres"
        }), " and the Salesforce destination's ID is ", _jsx(_components.code, {
          children: "production-salesforce"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Manifest files are ", _jsx(_components.strong, {
            children: "read-only"
          }), ". Git Sync overwrites any changes made to\nmanifest files. Use the Hightouch dashboard to create and edit sources and\ndestinations."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "aliases",
        children: "Aliases"
      }), "\n", _jsxs(_components.p, {
        children: ["Managing multiple Hightouch workspaces from the same Git repository can be difficult because source and destination IDs can differ between workspaces. ", _jsx(_components.strong, {
          children: "Alias files"
        }), " let you create custom IDs for sources and destinations in a given workspace allowing model and sync files to work across different workspaces."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch creates an empty alias file in your Git repository for each connected workspace. Alias filenames follow this format: ", _jsx(_components.code, {
          children: "aliases-<your-workspace-id>.yaml"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You can then add sources and destinations to the alias file following this format:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# e.g. aliases-hightouch-production-workspace.yaml"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "my-source-alias:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "my-actual-source-id"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "othersourcealias:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "another-workspace-source"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "destinations:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "my-destination-alias:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "existing-destination"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Each key is an alias and each value should be an actual source or destination ID. You can find source and destination IDs in the ", _jsx(_components.a, {
          href: "#manifests",
          children: "manifest file"
        }), " in the root of your Git repository."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Aliases should be unique across sources and destinations in the same\nworkspace. In other words, you can't use the same alias for multiple resources\nin the same alias file."
        })
      }), "\n", _jsx(_components.h4, {
        id: "example-alias-usage",
        children: "Example alias usage"
      }), "\n", _jsxs(_components.p, {
        children: ["Consider a workspace, ", _jsx(_components.code, {
          children: "acme-staging"
        }), ", with a model ", _jsx(_components.code, {
          children: "stocking-list.yaml"
        }), " file like this:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Stocking"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "list"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "source:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "staging-postgres"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "table"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "tableName:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "public.workspaces"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "isSchema:"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "primaryKey:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "id"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Another workspace called ", _jsx(_components.code, {
          children: "acme-production"
        }), " is connected to the same repository but can't use this model during Git Sync. That's because the ", _jsx(_components.code, {
          children: "acme-production"
        }), " workspace doesn't have a source with the ID ", _jsx(_components.code, {
          children: "staging-postgres"
        }), ". Instead, ", _jsx(_components.code, {
          children: "acme-production"
        }), " uses a Postgres source with the ID ", _jsx(_components.code, {
          children: "production-postgres"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can use aliases so that both workspaces can use the same model ", _jsx(_components.code, {
          children: "stocking-list.yaml"
        }), " file."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Add an alias to the ", _jsx(_components.code, {
            children: "acme-staging"
          }), " workspace alias file:"]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# aliases-acme-staging.yaml"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "aliased-postgres:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "staging-postgres"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This creates the custom ID ", _jsx(_components.code, {
          children: "aliased-postgres"
        }), " that points to the source with the ID ", _jsx(_components.code, {
          children: "staging-postgres"
        }), "."]
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Add an alias to the ", _jsx(_components.code, {
            children: "acme-production"
          }), " workspace alias file:"]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# aliases-acme-production.yaml"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "aliased-postgres:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "production-postgres"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This creates the custom ID ", _jsx(_components.code, {
          children: "aliased-postgres"
        }), " that points to the source with the ID ", _jsx(_components.code, {
          children: "production-postgres"
        }), ". For the next step, it's important that both alias files use the same alias name. Since these aliases exist in different workspaces, they are still unique within their respective workspaces."]
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Update the ", _jsx(_components.code, {
            children: "source"
          }), " value of ", _jsx(_components.code, {
            children: "stocking-list.yaml"
          }), " to ", _jsx(_components.code, {
            children: "aliased-postgres"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-diff",
          children: ["name: Stocking list\n", _jsx(_components.span, {
            className: "hljs-deletion",
            children: "- source: staging-postgres"
          }), "\n", _jsx(_components.span, {
            className: "hljs-addition",
            children: "+ source: aliased-postgres"
          }), "\ntype: table\ntableName: public.workspaces\nisSchema: false\nprimaryKey: id\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Now, during Git Sync, both workspaces resolve ", _jsx(_components.code, {
          children: "aliased-postgres"
        }), " to their respective Postgres slugs and Git Sync completes for both staging and production workspaces."]
      }), "\n", _jsx(_components.h2, {
        id: "validating-changes",
        children: "Validating changes"
      }), "\n", _jsxs(_components.p, {
        children: ["You can use Hightouch's ", _jsx(_components.strong, {
          children: "CI checks"
        }), " to validate changes in your Git Sync linked repository. This feature checks your pull requests to ensure they don't break any existing syncs in Hightouch, and that the changes are formatted correctly. To enable this, toggle on ", _jsx(_components.strong, {
          children: "GitHub Pull Request checks"
        }), " in your Git Sync ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/git-sync/configuration",
          children: "configuration page"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Once enabled, Hightouch runs a CI check on any pull request made to the connected Git repository. If a breaking change is detected—for example, deleting a model used in a sync—the CI check fails. Clicking into the details of a failed CI check shows which sync the breaking change effects."
      }), "\n", _jsx(_components.p, {
        children: "The CI check passes if the pull request doesn't affect any Hightouch syncs."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/passed-code-check.png",
          alt: "Passed CI check"
        })
      }), "\n", _jsx(_components.h2, {
        id: "changelog",
        children: "Changelog"
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["While Git Sync provides a changelog through commit history, you can also view\n", _jsx(_components.a, {
            href: "/workspace-management/audit-logs",
            children: "audit logs"
          }), " for an in-app changelog."]
        })
      }), "\n", _jsx(_components.p, {
        children: "You can use your Git repository as a changelog to track which user created or modified which resource."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "A user's Hightouch email must match their GitHub email to show the correct\nuser who created or edited resources."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you can see the list of commits, when they were made, and who made them, by going to the repository's ", _jsx(_components.strong, {
          children: "Commits"
        }), " tab or History."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-audit-log.png",
          alt: "Commit logs in GitHub"
        })
      }), "\n", _jsx(_components.p, {
        children: "By clicking on a single commit, you can see the exact changes line-by-line."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-audit-log-detail.png",
          alt: "File diff in GitHub"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tunneling",
        children: "Tunneling"
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to use a Git repository that's hosted on your on-premise server without public internet access, you can use ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "tunneling"
        }), " to connect to your Git server. Once you've set up the tunnel, Hightouch uses it to connect to your Git repository."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to the ", _jsxs(_components.a, {
              href: "https://app.hightouch.com/settings/tunnels",
              children: [_jsx(_components.strong, {
                children: "Tunnels"
              }), " tab"]
            }), " on the ", _jsx(_components.strong, {
              children: "Settings"
            }), " page, and create a tunnel or reverse tunnel that connects to your Git server. Refer to the ", _jsx(_components.a, {
              href: "/security/ssh-tunneling",
              children: "tunneling documentation"
            }), " for further instructions."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to the ", _jsxs(_components.a, {
              href: "https://app.hightouch.com/extensions/git-sync/configuration",
              children: [_jsx(_components.strong, {
                children: "Git Sync"
              }), " configuration page"]
            }), " and click ", _jsx(_components.strong, {
              children: "Manage"
            }), " next to ", _jsx(_components.strong, {
              children: "Git credentials"
            }), " under ", _jsx(_components.strong, {
              children: "Configuration"
            }), ". Add your credential and select the correct tunnel to use."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["It's recommended to choose SSH protocol and ", _jsx(_components.code, {
            children: "ssh_privatekey"
          }), " to connect to a\nGit repository, as basic auth may have problems forwarding the authentication\nheader through an ssh tunnel and HTTP redirects. You can find more detail in\nthe ", _jsx(_components.a, {
            href: "https://docs.github.com/en/authentication/connecting-to-github-with-ssh",
            children: "GitHub\ndocumentation"
          }), "\non how to use ssh protocol to authenticate. Hightouch doesn't support\n", _jsx(_components.code, {
            children: "ssh_privatekey"
          }), " with passphrases."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-repo-ssh-tunnel.png",
          alt: "Entering Git credentials in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsx(_components.p, {
        children: "From here, you have the full power of Git in your hands. Here are some potential\nideas to try next:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Create a new model by copying an existing model and changing the name and query"
        }), "\n", _jsx(_components.li, {
          children: "Create a new model and sync at the same time"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "what-does-the-full-resync-button-do",
        children: "What does the Full resync button do?"
      }), "\n", _jsxs(_components.p, {
        children: ["Clicking the ", _jsx(_components.strong, {
          children: "Full resync"
        }), " button runs an outbound sync for all supported resources in the workspace. This action pushes all workspace syncs, models, and event contracts to Git, and then pulls changes from Git back to your workspace, in that order."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-outbound.png",
          alt: "Git Sync diagram"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If your Git repository has changes that aren't yet reflected in your Hightouch workspace, clicking ", _jsx(_components.strong, {
          children: "Full resync"
        }), " overwrites those changes."]
      }), "\n", "\n", _jsx(_components.h3, {
        id: "recreating-a-deleted-yaml-file",
        children: "Recreating a deleted YAML file"
      }), "\n", _jsxs(_components.p, {
        children: ["As explained in the ", _jsx(_components.a, {
          href: "#configure-git-sync",
          children: "Git Sync configuration"
        }), " section, this extension doesn't support deletions from Git.\nIf you delete a YAML file in Git, the corresponding model or sync remains in Hightouch.\nThe file won't be rewritten to Git until the next change in Hightouch, or unless you trigger a ", _jsx(_components.a, {
          href: "#what-does-the-full-resync-button-do",
          children: "full resync"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to recreate a deleted YAML file in your Git repository, click the ", _jsx(_components.strong, {
          children: "Full resync"
        }), " button.\nIf you want to do this manually, you can create a new YAML file in your Git repository with the same file name and ", _jsx(_components.a, {
          href: "#schemas",
          children: "schema"
        }), " as the original one.\nHightouch understands that it refers to an existing model or sync and it associates the new YAML file to the existing resource."]
      }), "\n", _jsx(_components.h3, {
        id: "protected-branch-update-failed",
        children: "Protected branch update failed"
      }), "\n", _jsxs(_components.p, {
        children: ["If you selected a protected ", _jsx(_components.a, {
          href: "#configure-git-sync",
          children: "branch"
        }), " in your configuration, your Git Sync setup might fail with this error: ", _jsx(_components.code, {
          children: "Protected branch update failed - Changes must be made through a pull request"
        }), ". To resolve this, select a different branch that isn't protected and create pull requests in GitHub to merge the updates to the protected branch."]
      }), "\n", _jsxs(_components.p, {
        children: ["Otherwise, you can see our instructions ", _jsx(_components.a, {
          href: "#using-git-sync-with-branch-protection",
          children: "here"
        }), " for how to use Hightouch Git Sync with branch protection."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
